import { useEffect, useId, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { cn } from '../../lib/cn';
import { sheetShouldClose } from '../../lib/sheet';

export interface BottomSheetProps {
  open: boolean;
  onClose(): void;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select, [tabindex]:not([tabindex="-1"])';
const EASE = [0.2, 0, 0, 1] as const;
/** A press becomes a drag after this many pixels down (shorter moves stay taps on buttons). */
const DRAG_START_PX = 6;
/** The sheet follows the finger at this rate: it feels held, as in a native sheet. */
const ELASTIC = 0.6;

function isScrollable(el: HTMLElement): boolean {
  return el.scrollHeight > el.clientHeight;
}

interface Drag {
  pointerId: number;
  startY: number;
  dragging: boolean;
  lastY: number;
  lastT: number;
  velocity: number;
}

/**
 * A sheet that slides up from the bottom over everything (menu included).
 * Closes on a swipe down, a tap on the backdrop and Escape.
 *
 * The swipe is plain pointer events, not motion's drag: drag needs motion's
 * domMax bundle (+13 KB gzip), the bundle budget has no room for it.
 */
export function BottomSheet({ open, onClose, title, children, className }: BottomSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    // Content taller than the sheet scrolls by touch and only the head drags; short content drags anywhere.
    if (panel) panel.style.touchAction = isScrollable(panel) ? 'pan-y' : 'none';
    panel?.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose]);

  function setOffset(px: number, animated: boolean) {
    const panel = panelRef.current;
    if (!panel) return;
    panel.style.transition = animated ? 'translate 200ms cubic-bezier(0.2, 0, 0, 1)' : 'none';
    panel.style.translate = px > 0 ? `0 ${px}px` : '';
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    const panel = panelRef.current;
    if (panel && isScrollable(panel) && !headRef.current?.contains(e.target as Node)) return;
    drag.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      dragging: false,
      lastY: e.clientY,
      lastT: e.timeStamp,
      velocity: 0,
    };
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const offset = e.clientY - d.startY;
    if (!d.dragging) {
      if (offset < DRAG_START_PX) return;
      d.dragging = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) d.velocity = ((e.clientY - d.lastY) / dt) * 1000;
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    setOffset(Math.max(0, offset) * ELASTIC, false);
  }

  function onPointerEnd(e: ReactPointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (!d.dragging) return;
    const offset = e.clientY - d.startY;
    // A finger resting before release is not a flick.
    const velocity = e.timeStamp - d.lastT > 100 ? 0 : d.velocity;
    if (sheetShouldClose(offset, velocity, panelRef.current?.offsetHeight ?? 0)) onClose();
    else setOffset(0, true);
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <m.div
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <m.div
            className="absolute inset-x-0 bottom-0 mx-auto max-w-lg"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.28, ease: EASE }}
          >
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? titleId : undefined}
              className={cn(
                'bg-surface rounded-t-2xl px-4 pt-2 select-none',
                'pb-[max(16px,env(safe-area-inset-bottom))] max-h-[90vh] overflow-y-auto',
                className,
              )}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerEnd}
              onPointerCancel={onPointerEnd}
            >
              <div ref={headRef} className="touch-none pb-3">
                <div aria-hidden className="mx-auto mb-3 h-1 w-10 rounded-full bg-line" />
                {title && (
                  <h2 id={titleId} className="text-lg font-bold">
                    {title}
                  </h2>
                )}
              </div>
              {children}
            </div>
          </m.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
